import { AUTH, HOSTED_APP, STATIC_ACCESS_TOKEN } from '@/config/settings'
import { Auth0Provider as _Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { useEffect, type ReactNode } from 'react'
import { clearAccessToken, getAccessToken, saveAccessToken } from './auth'
import { useTokenStore } from './token-store'

// Read ONCE at module load instead of on every render. Auth0 needs it twice, as the login
// `redirect_uri` and as the logout `returnTo`, and both must be the same value. It cannot change
// without a full page load, so reading it during render buys nothing and puts a mutable-global read
// in the render path — which is what makes the React Compiler stop memoising a component. Nothing
// flags this today; it is the constraint the project set for itself, applied to the outermost
// provider in the tree.
const APP_ORIGIN = window.location.origin

export function Auth0Provider({ children }: { children: ReactNode }) {
  // Hosted mode has no Auth0 session and must not mount the provider: its cleanup effect reads
  // "token present + not Auth0-authenticated" as a stale session and would clear the hosted token
  // and force a logout redirect. The platform already authenticated the viewer (carto-info.json).
  if (STATIC_ACCESS_TOKEN || HOSTED_APP) {
    return children
  }
  const { domain, clientId, authorizationParams } = AUTH

  if (!clientId) {
    // eslint-disable-next-line no-console
    console.error(
      'Need to define a clientId. Please set a valid client id in the VITE_CLIENT_ID variable in the .env file.',
    )
  }

  return (
    <_Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        ...authorizationParams,
        redirect_uri: APP_ORIGIN,
      }}
      useRefreshTokens={true}
      cacheLocation='localstorage'
    >
      <Auth>{children}</Auth>
    </_Auth0Provider>
  )
}

function Auth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } =
    useAuth0()
  const accessToken = getAccessToken()

  useEffect(() => {
    const _logout = () =>
      logout({
        logoutParams: {
          returnTo: APP_ORIGIN,
        },
      })

    if (!isAuthenticated) {
      if (!accessToken || isLoading) return

      clearAccessToken()
      void _logout()

      return
    }

    // The token refresh happens IN THE BACKGROUND: the render is not blocked. `setLoading(true)`
    // plus `return null` used to unmount the WHOLE app, deck.gl's WebGL context included, on every
    // authenticated reload, even with the token cached (a flash to white plus a remount of the map).
    // Now the render goes ahead; the data surfaces are gated on `ready` (token-store), which is set
    // to `true` once the JWT is CONFIRMED, so catalogues and tiles request with the token and never
    // with an empty Bearer.
    const fetchAccessToken = async () => {
      try {
        const response = await getAccessTokenSilently()
        saveAccessToken(response)
        useTokenStore.getState().setReady(true)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching access token:', error)
        clearAccessToken()
        void _logout()
      }
    }

    void fetchAccessToken()
  }, [accessToken, getAccessTokenSilently, isAuthenticated, isLoading, logout])

  return children
}
